import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, ChevronRight, ShieldAlert, ExternalLink, Award, FileDown, Filter, Check, Edit3, Trash2, Clock, Plus, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import CertificacaoCertificar from './CertificacaoCertificar';
import CertificacaoQuestionario from './CertificacaoQuestionario';
import SelecaoPecaCentral from './SelecaoPecaCentral';
import useEhCompacto from '../../../utils/useEhCompacto';
import ImagemSharePoint from '../../ImagemSharePoint';
import { aguardandoDe, diasParado, emAndamento, ordenarPorParado } from './certificacao-fila-utils';
import { etapaDe, resumirFila, resumirPorEquipamento, equipamentosNoPrograma, equipamentosForaDoPrograma } from './certificacao-placar-utils';
import PlacarCertificacao from './PlacarCertificacao';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const STATUS_OPCOES = ['Rascunho', 'Em diagnóstico', 'Em execução', 'Certificado com Ressalvas', 'Certificado', 'Cancelado'];

// =====================================================================
// Consulta dos relatórios de certificação gravados (cabeçalho + detalhe).
//
// ⚠️ Esta tela se chama "relatórios gravados", mas o que ela contém é FILA DE
// TRABALHO: medido em 17/08/2026, 22 dos 24 relatórios estavam em andamento e
// apenas 2 certificados. Por isso ela abre em "Em andamento", ordenada pelo mais
// parado, e traz a coluna "Aguardando" — quem está segurando cada relatório.
// A leitura de fila (aguardando/parado) mora em `certificacao-fila-utils.js`,
// pura e testada; aqui fica só a apresentação.
//
// Até 2026-08-17 a lista era uma ÁRVORE Loja → Equipamento → Relatório, fechada
// por padrão: eram 2 cliques até enxergar qualquer linha e nada distinguia um
// diagnóstico intocado de um que só espera a assinatura do gerente (12 dos 20,
// na medição). Com mais equipamentos entrando no programa, a árvore só ficaria
// mais funda — daí a lista plana.
// =====================================================================

const API = '/api/manutencao/certificacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const statusStyle = (s) => ({
  'Rascunho': { bg: '#ede9fe', color: '#6d28d9' },
  'Em diagnóstico': { bg: '#fef3c7', color: '#92400e' },
  'Em execução': { bg: '#dbeafe', color: '#1d4ed8' },
  // Provisório (aguardando validação): teal, distinto do âmbar do diagnóstico e do verde do certificado.
  'Certificado com Ressalvas': { bg: '#ccfbf1', color: '#0f766e' },
  'Certificado': { bg: '#d1fae5', color: '#065f46' },
  'Cancelado': { bg: '#e2e8f0', color: '#475569' },
}[s] || { bg: '#e2e8f0', color: '#475569' });

// Rótulo curto do status — para os chips de contagem na linha da loja (onde o
// nome longo "Certificado com Ressalvas" não cabe).
const STATUS_CURTO = {
  'Rascunho': 'Rascunho',
  'Em diagnóstico': 'Diagnóstico',
  'Em execução': 'Execução',
  'Certificado com Ressalvas': 'C/ Ressalvas',
  'Certificado': 'Certificado',
  'Cancelado': 'Cancelado',
};

// Deixa claro em que ponto o relatório está (Etapa 1 só, ou Etapa 2).
const descricaoEtapa = (s) => ({
  'Rascunho': 'Rascunho — Etapa 1 (diagnóstico) em andamento, ainda não finalizado',
  'Em diagnóstico': 'Só Etapa 1 (diagnóstico) — certificação pendente',
  'Em execução': 'Etapa 2 em andamento (rascunho)',
  'Certificado com Ressalvas': 'Etapa 2 concluída — aguardando validação de gerente e consultor',
  'Certificado': 'Certificado — validado por gerente e consultor',
  'Cancelado': 'Cancelado',
}[s] || '');

const corSaude = (c) => ({
  Excelente: '#059669', 'Muito Bom': '#4f46e5', Bom: '#65a30d', 'Atenção': '#d97706', 'Crítico': '#dc2626',
}[c] || '#64748b');

const CLASSIF_LABEL = { 2: 'Conforme', 1: 'Desgaste', 0: 'Crítico', 3: 'N/A' };
const CLASSIF_COR = { 2: '#059669', 1: '#b45309', 0: '#b91c1c', 3: '#64748b' };

const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

// Célula base da tabela de relatórios (cabeçalho e corpo).
const thBase = { padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontSize: 12.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdBase = { padding: '12px 16px', fontSize: 14, color: '#0f172a', verticalAlign: 'middle' };

// Cor da coluna "Aguardando": distingue quem está devendo. Validação pendente é
// âmbar (alguém precisa assinar); trabalho em curso é índigo — a cor do módulo,
// porque é a própria Manutenção que está com ele.
const AGUARDANDO_ESTILO = {
  validacao: { bg: '#fef3c7', color: '#92400e' },
  trabalho: { bg: '#eef2ff', color: '#4338ca' },
  nenhum: { bg: 'transparent', color: '#cbd5e1' },
};

// Cor do tempo parado. Os cortes saem da medição de 17/08/2026, em que a fila ia
// de 3 a 39 dias: acima de 30 é caso perdido de vista, acima de 14 já pede ação.
// Só colore a fila — relatório encerrado não está "parado", está pronto.
const corParado = (dias) => (dias > 30 ? '#b91c1c' : dias > 14 ? '#b45309' : '#64748b');

// Abas: a fila (o que exige ação) separada do arquivo (o que já terminou).
// ⚠️ 'Cancelado' não entra em nenhuma das duas — não é fila nem conquista; ele
// aparece em "Todos", que é o escape e fecha a contagem com o total.
const ABAS = [
  { chave: 'andamento', label: 'Em andamento', filtro: emAndamento },
  { chave: 'certificados', label: 'Certificados', filtro: (r) => r.STATUS === 'Certificado' },
  { chave: 'todos', label: 'Todos', filtro: () => true },
];

// `acoesExtras`: nó opcional com botões de ação renderizado no cabeçalho (ex.:
// "Novo Diagnóstico" + botões de admin, quando esta tela é a inicial do sub-hub).
// `onVoltar` / `rotuloVoltar`: a volta para a FRENTE do hub que contém o
// programa. Opcionais — sem eles a tela simplesmente não mostra a seta.
const CertificacaoHistorico = ({ acoesExtras, onVoltar, rotuloVoltar }) => {
  const compacto = useEhCompacto();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const perfil = perfilNoModulo(user?.perfil, 'manutencao') || '';
  const meuId = user?.id;
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [detalheId, setDetalheId] = useState(null);
  const [certificarId, setCertificarId] = useState(null);
  // Rascunho da Etapa 1 sendo retomado (id do relatório + template p/ abrir o form).
  const [continuarRascunho, setContinuarRascunho] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null); // rascunho em exclusão (trava o botão)

  // Filtros (client-side — a lista costuma ser pequena e o filtro é instantâneo).
  const [aba, setAba] = useState('andamento'); // abre na FILA, não no arquivo
  const [fLoja, setFLoja] = useState('');
  const [fEquip, setFEquip] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fPeriodo, setFPeriodo] = useState(''); // mês de referência 'AAAA-MM' (input type=month)
  // Dois recortes que o STATUS não sabe expressar, e que são o que o placar
  // pergunta: "de quem é a bola?" (validação × Manutenção) e "que etapa?".
  // ⚠️ Ambos aparecem na barra de filtros: número clicado que filtra em silêncio
  // deixa a pessoa com uma lista curta e nenhum jeito de desfazer.
  const [fAguardando, setFAguardando] = useState(''); // '' | 'validacao' | 'trabalho'
  const [fEtapa, setFEtapa] = useState('');           // '' | 1 | 2

  // O parque de equipamentos (dtbCertAtivo), agregado por tipo. É o DENOMINADOR
  // da cobertura — a lista de relatórios não sabe quantos equipamentos existem
  // na rede. Falha silenciosa de propósito: sem ele o painel por equipamento
  // ainda conta relatórios, só perde a barra de cobertura.
  const [parque, setParque] = useState([]);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(API, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setLista(await r.json());
    } catch (e) {
      setErro('Erro ao carregar os relatórios.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Carga única: o parque não muda com filtro de tela.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/parque`, { headers: auth() });
        if (r.ok) setParque(await r.json());
      } catch (e) {
        // Sem parque a tela não quebra — ver a nota no useState acima.
        console.error('[certificacao] parque:', e);
      }
    })();
  }, []);

  // Opções de loja/equipamento derivadas dos dados (status é enum fixo).
  const lojas = useMemo(() => [...new Set(lista.map((r) => r.LOJA).filter(Boolean))].sort(), [lista]);
  const equipamentos = useMemo(() => [...new Set(lista.map((r) => r.EQUIPAMENTO).filter(Boolean))].sort(), [lista]);

  // Data de referência do relatório (certificação; cai na criação se vazia).
  const dataRef = (r) => String(r.DATA_CERTIFICACAO || r.DATA_CRIACAO || '').slice(0, 10);

  // Base: tudo o que passa nos filtros de recorte (loja/equipamento/período).
  // O status fica FORA daqui de propósito — é ele que alimenta as contagens das
  // abas, que precisam continuar corretas mesmo com um status selecionado.
  const baseFiltrada = useMemo(() => lista.filter((r) => {
    if (fLoja && r.LOJA !== fLoja) return false;
    if (fEquip && r.EQUIPAMENTO !== fEquip) return false;
    const d = dataRef(r);
    // Período = mês de referência (AAAA-MM): compara só o prefixo ano-mês.
    if (fPeriodo && (!d || d.slice(0, 7) !== fPeriodo)) return false;
    return true;
  }), [lista, fLoja, fEquip, fPeriodo]);

  const contagemAba = useMemo(() => Object.fromEntries(
    ABAS.map((a) => [a.chave, baseFiltrada.filter(a.filtro).length])
  ), [baseFiltrada]);

  // Só os status que existem na aba corrente vão para o select — oferecer
  // "Certificado" dentro da fila renderia lista vazia sem explicar por quê.
  const statusDaAba = useMemo(() => {
    const daAba = baseFiltrada.filter(ABAS.find((a) => a.chave === aba).filtro);
    return STATUS_OPCOES.filter((s) => daAba.some((r) => r.STATUS === s));
  }, [baseFiltrada, aba]);

  // Troca de aba: derruba o status escolhido se ele não existir na aba nova
  // (senão a tela abriria vazia com um filtro invisível ligado).
  const trocarAba = (chave) => {
    setAba(chave);
    // "Aguardando" e "Etapa" são leituras da FILA: num relatório certificado
    // não existe etapa em curso nem assinatura pendente, e manter o filtro
    // ligado abriria a aba vazia sem dizer por quê.
    if (chave === 'certificados') { setFAguardando(''); setFEtapa(''); }
    setFStatus((s) => (s && !baseFiltrada.some((r) => ABAS.find((a) => a.chave === chave).filtro(r) && r.STATUS === s) ? '' : s));
  };

  // A lista que vai à tela. Na FILA, o mais parado no topo — é a ordem que a
  // torna útil. No arquivo, o mais recente primeiro: lá "parado há" não quer
  // dizer nada, e o que se procura é a última certificação.
  const listaFiltrada = useMemo(() => {
    const visivel = baseFiltrada
      .filter(ABAS.find((a) => a.chave === aba).filtro)
      .filter((r) => !fStatus || r.STATUS === fStatus)
      // "De quem é a bola" atravessa vários status — é por isso que não dá para
      // fazer este recorte com o select de status (ver aguardandoDe).
      .filter((r) => !fAguardando || aguardandoDe(r).tipo === fAguardando)
      .filter((r) => !fEtapa || etapaDe(r.STATUS) === Number(fEtapa));
    return aba === 'certificados'
      ? visivel.slice().sort((a, b) => dataRef(b).localeCompare(dataRef(a)))
      : ordenarPorParado(visivel);
  }, [baseFiltrada, aba, fStatus, fAguardando, fEtapa]);

  const temFiltro = fLoja || fEquip || fStatus || fPeriodo || fAguardando || fEtapa;
  const limparFiltros = () => {
    setFLoja(''); setFEquip(''); setFStatus(''); setFPeriodo(''); setFAguardando(''); setFEtapa('');
  };

  // ---------------------------------------------------------------
  // O PLACAR (ver PlacarCertificacao.js + certificacao-placar-utils.js).
  //
  // ⚠️ DUAS BASES DIFERENTES, e a diferença é deliberada:
  //   • a fila (em aberto / validação / etapa) mede o RECORTE atual — o mesmo
  //     `baseFiltrada` das abas, sem os filtros que o próprio placar aplica
  //     (senão clicar num bloco reduziria o placar a ele mesmo);
  //   • a cobertura por equipamento é ACUMULADA: ignora equipamento e período,
  //     porque "quanto do parque já passou pelo programa" é uma pergunta do
  //     programa inteiro, e porque o painel precisa continuar navegável depois
  //     de escolher um equipamento (senão sobra um card só, sem volta).
  // O recorte por LOJA vale para as duas — inclusive para o denominador.
  // ---------------------------------------------------------------
  const placar = useMemo(() => resumirFila(baseFiltrada), [baseFiltrada]);

  const porEquipamento = useMemo(() => {
    const soLoja = lista.filter((r) => !fLoja || r.LOJA === fLoja);
    return resumirPorEquipamento(soLoja, parque, fLoja);
  }, [lista, parque, fLoja]);

  // Aplica o patch de filtro que veio de um bloco do placar. Campos ausentes
  // ficam como estão (é patch, não substituição).
  const filtrarPeloPlacar = (patch) => {
    if ('aba' in patch) trocarAba(patch.aba);
    if ('status' in patch) setFStatus(patch.status);
    if ('aguardando' in patch) setFAguardando(patch.aguardando);
    // Guarda sempre como TEXTO: o select da barra devolve string e o bloco do
    // placar devolve número — misturar os dois apaga a marcação de selecionado.
    if ('etapa' in patch) setFEtapa(patch.etapa === '' ? '' : String(patch.etapa));
    if ('equipamento' in patch) setFEquip(patch.equipamento);
  };

  // O texto do recorte, para o placar não se passar por número da rede.
  const escopoTexto = [
    fLoja && `loja ${fLoja}`,
    fEquip,
    fPeriodo && `período ${fPeriodo.split('-').reverse().join('/')}`,
  ].filter(Boolean).join(' · ');

  // Exclui DE VEZ um rascunho (o back só permite STATUS='Rascunho' + autor/Admin).
  // Pede confirmação; ao excluir, limpa o ponteiro do banner se apontar p/ ele.
  const excluirRascunho = async (r, e) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir o rascunho #${r.ID} (${r.EQUIPAMENTO} · ${r.LOJA})? Esta ação não pode ser desfeita.`)) return;
    setExcluindoId(r.ID);
    try {
      const resp = await fetch(`${API}/${r.ID}`, { method: 'DELETE', headers: auth() });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(d?.message || 'Erro ao excluir.');
      try {
        const raw = localStorage.getItem('cert_rascunho_etapa1');
        if (raw && JSON.parse(raw)?.id === r.ID) localStorage.removeItem('cert_rascunho_etapa1');
      } catch { /* ignora */ }
      await carregar();
    } catch (err) { alert(err.message); }
    finally { setExcluindoId(null); }
  };

  const exportarExcel = () => {
    const data10 = (d) => (d ? String(d).slice(0, 10) : '');
    const linhas = listaFiltrada.map((r) => ({
      'Relatório': r.ID, 'Equipamento': r.EQUIPAMENTO, 'Versão': r.NOME_VERSAO, 'Loja': r.LOJA,
      'Patrimônio': r.NUMERO_ATIVO || '', 'Fabricante': r.FABRICANTE || '', 'Modelo': r.MODELO || '',
      'Status': r.STATUS, 'Etapa': descricaoEtapa(r.STATUS),
      // As duas colunas da leitura de fila — é por elas que se cobra.
      'Aguardando': aguardandoDe(r).texto,
      'Parado há (dias)': emAndamento(r) ? diasParado(r) : '',
      'Índice de Saúde (%)': r.INDICE_SAUDE, 'Classificação': r.CLASSIFICACAO_SAUDE || '',
      'Resultado': r.RESULTADO_CERTIFICACAO || '', 'Validade': data10(r.DATA_VALIDADE),
      'Data Certificação': data10(r.DATA_CERTIFICACAO),
      'Gerente de Negócios': r.GERENTE_NEGOCIOS || '', 'Gerente de Plantão': r.GERENTE_PLANTAO || '',
      'Criado em': data10(r.DATA_CRIACAO),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), 'Certificações');
    XLSX.writeFile(wb, `Certificacoes_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Retomar rascunho da Etapa 1 → abre o questionário hidratado pelo relatório.
  if (continuarRascunho) return <CertificacaoQuestionario idTemplate={continuarRascunho.idTemplate} idRascunho={continuarRascunho.id} onVoltar={() => { setContinuarRascunho(null); carregar(); }} />;
  if (certificarId) return <CertificacaoCertificar idRelatorio={certificarId} onVoltar={() => { setCertificarId(null); carregar(); }} />;
  if (detalheId) return <Detalhe id={detalheId} onVoltar={() => setDetalheId(null)} onCertificar={(id) => { setDetalheId(null); setCertificarId(id); }} />;

  // No compacto (celular) cada filtro ocupa a linha inteira — senão os selects
  // ficam espremidos e cortados. No desktop, largura automática lado a lado.
  const selStyle = {
    padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff',
    ...(compacto ? { width: '100%', boxSizing: 'border-box' } : {}),
  };
  const filtroItem = compacto ? { flex: '1 1 100%' } : {};

  return (
    <div>
      {/* Volta para a frente do hub (hoje "Manutenção Equipamento"). Fica ACIMA
          do título e não ao lado dele: ao lado, disputaria a linha com os botões
          de ação ("Novo Diagnóstico", "Equipamentos"…) e, no celular, a seta
          quebraria para baixo deles. ⚠️ Leva o NOME do destino — seta pelada
          obriga a pessoa a clicar para descobrir para onde vai. É o mesmo
          padrão do "← Visão Geral" do sub-hub. */}
      {onVoltar && (
        <button
          onClick={onVoltar}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none',
            background: 'transparent', color: '#64748b', fontWeight: 600,
            fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#4f46e5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; }}
        >
          <ArrowLeft size={16} /> {rotuloVoltar || 'Voltar'}
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 180, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Relatórios gravados</h3>
        {acoesExtras}
        {listaFiltrada.length > 0 && (
          <button onClick={exportarExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <FileDown size={16} /> Exportar Excel
          </button>
        )}
      </div>

      {/* PLACAR — a leitura que a tela não dava: quantos em aberto, quantos
          esperam assinatura (e de quem), em que etapa está o trabalho e como
          vai cada equipamento. Vem ANTES das abas porque é o resumo de onde se
          escolhe o que abrir; cada bloco filtra a lista abaixo. */}
      {!carregando && (
        <PlacarCertificacao
          resumo={placar}
          porEquipamento={equipamentosNoPrograma(porEquipamento)}
          foraDoPrograma={equipamentosForaDoPrograma(porEquipamento)}
          selecao={{ aba, etapa: fEtapa, aguardando: fAguardando, equipamento: fEquip }}
          onFiltrar={filtrarPeloPlacar}
          escopo={escopoTexto}
          periodoAtivo={!!fPeriodo}
          compacto={compacto}
        />
      )}

      {/* Abas: a fila primeiro, o arquivo depois. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {ABAS.map((a) => {
          const ativa = aba === a.chave;
          return (
            <button key={a.chave} onClick={() => trocarAba(a.chave)} style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: ativa ? 800 : 600, fontSize: 14, color: ativa ? '#4f46e5' : '#64748b',
              borderBottom: `2px solid ${ativa ? '#4f46e5' : 'transparent'}`, marginBottom: -1,
            }}>
              {a.label} <span style={{ fontVariantNumeric: 'tabular-nums', color: ativa ? '#4f46e5' : '#94a3b8' }}>({contagemAba[a.chave]})</span>
            </button>
          );
        })}
      </div>

      {/* Barra de filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 16 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13 }}><Filter size={15} /> Filtros</span>
        <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={selStyle}>
          <option value="">Todas as lojas</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={fEquip} onChange={(e) => setFEquip(e.target.value)} style={selStyle}>
          <option value="">Todos os equipamentos</option>
          {equipamentos.map((eq) => <option key={eq} value={eq}>{eq}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={selStyle}>
          <option value="">Todos os status</option>
          {statusDaAba.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* Espelhos dos blocos do placar. Existem na barra para o filtro
            aplicado por um clique lá em cima poder ser VISTO e desfeito aqui. */}
        <select value={fAguardando} onChange={(e) => setFAguardando(e.target.value)} style={selStyle} title="De quem é a bola">
          <option value="">Aguardando: qualquer um</option>
          <option value="validacao">Aguardando validação</option>
          <option value="trabalho">Com a Manutenção</option>
        </select>
        <select value={fEtapa} onChange={(e) => setFEtapa(e.target.value)} style={selStyle} title="Etapa do programa">
          <option value="">Todas as etapas</option>
          <option value="1">Etapa 1 · Diagnóstico</option>
          <option value="2">Etapa 2 · Certificação</option>
        </select>
        <label style={{ fontSize: 13, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6, ...filtroItem }}>Período <input type="month" value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)} style={selStyle} /></label>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Limpar</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94a3b8' }}>{listaFiltrada.length} de {lista.length}</span>
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {carregando ? <p style={{ color: '#64748b' }}>Carregando…</p> : lista.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum relatório gravado ainda.
        </div>
      ) : listaFiltrada.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          {aba === 'andamento' && !temFiltro
            ? 'Nenhum relatório em andamento — a fila está limpa.'
            : 'Nenhum relatório para os filtros escolhidos.'}
        </div>
      ) : compacto ? (
        // ── Celular: um cartão por relatório. Oito colunas não cabem numa tela de
        // mão, e é no celular que o gerente valida — a pendência tem de estar
        // legível sem rolagem lateral.
        <div style={{ display: 'grid', gap: 10 }}>
          {listaFiltrada.map((r) => {
            const st = statusStyle(r.STATUS);
            const ag = aguardandoDe(r);
            const agEstilo = AGUARDANDO_ESTILO[ag.tipo];
            const dias = emAndamento(r) ? diasParado(r) : null;
            const podeMexer = r.STATUS === 'Rascunho' && (r.USUARIO_ID === meuId || perfil === 'Admin');
            return (
              <div key={r.ID} onClick={() => setDetalheId(r.ID)} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{r.LOJA}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#334155' }}>{r.EQUIPAMENTO}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>#{r.ID}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: st.bg, color: st.color }}>{STATUS_CURTO[r.STATUS] || r.STATUS}</span>
                  {ag.tipo !== 'nenhum' && (
                    <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: agEstilo.bg, color: agEstilo.color }}>{ag.texto}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5, color: '#64748b' }}>
                  {dias !== null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: corParado(dias), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      <Clock size={13} /> parado há {dias} {dias === 1 ? 'dia' : 'dias'}
                    </span>
                  )}
                  {r.STATUS !== 'Rascunho' && r.INDICE_SAUDE != null && (
                    <span style={{ fontWeight: 800, color: corSaude(r.CLASSIFICACAO_SAUDE), fontVariantNumeric: 'tabular-nums' }}>{r.INDICE_SAUDE}%</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{dataRef(r) ? dataRef(r).split('-').reverse().join('/') : ''}</span>
                </div>
                {podeMexer && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setContinuarRascunho({ id: r.ID, idTemplate: r.ID_TEMPLATE }); }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>
                      <Edit3 size={13} /> Continuar
                    </button>
                    <button
                      onClick={(e) => excluirRascunho(r, e)} disabled={excluindoId === r.ID}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 800, fontSize: 12.5, cursor: excluindoId === r.ID ? 'wait' : 'pointer' }}>
                      <Trash2 size={13} /> {excluindoId === r.ID ? 'Excluindo…' : 'Excluir'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ ...thBase, textAlign: 'left' }}>Loja</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Equipamento</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Nº</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Status</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Aguardando</th>
                  {/* Só na fila: relatório encerrado não está parado, está pronto. */}
                  {aba !== 'certificados' && <th style={{ ...thBase, textAlign: 'right' }}>Parado há</th>}
                  <th style={{ ...thBase, textAlign: 'right' }}>Índice de Saúde</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Data</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.map((r) => {
                  const st = statusStyle(r.STATUS);
                  const ag = aguardandoDe(r);
                  const agEstilo = AGUARDANDO_ESTILO[ag.tipo];
                  const dias = emAndamento(r) ? diasParado(r) : null;
                  const podeMexer = r.STATUS === 'Rascunho' && (r.USUARIO_ID === meuId || perfil === 'Admin');
                  return (
                    <tr key={r.ID} onClick={() => setDetalheId(r.ID)} title={descricaoEtapa(r.STATUS)}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#fff' }}>
                      <td style={{ ...tdBase, fontWeight: 800 }}>{r.LOJA}</td>
                      <td style={tdBase}>
                        <div style={{ fontWeight: 700, color: '#334155', fontSize: 13.5 }}>{r.EQUIPAMENTO}</div>
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                          {r.NOME_VERSAO}{r.NUMERO_ATIVO ? ` · Ativo ${r.NUMERO_ATIVO}` : ''}
                        </div>
                      </td>
                      <td style={{ ...tdBase, color: '#94a3b8', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>#{r.ID}</td>
                      <td style={tdBase}>
                        {/* Rótulo curto: "Certificado com Ressalvas" estoura a coluna.
                            O nome inteiro e a explicação da etapa ficam no title da linha. */}
                        <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>
                          {STATUS_CURTO[r.STATUS] || r.STATUS}
                        </span>
                      </td>
                      <td style={tdBase}>
                        {ag.tipo === 'nenhum' ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                          <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, background: agEstilo.bg, color: agEstilo.color, whiteSpace: 'nowrap' }}>
                            {ag.texto}
                          </span>
                        )}
                      </td>
                      {aba !== 'certificados' && (
                        <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {dias === null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', color: corParado(dias), fontWeight: dias > 14 ? 800 : 600 }}>
                              {dias > 14 && <Clock size={13} />}{dias} {dias === 1 ? 'dia' : 'dias'}
                            </span>
                          )}
                        </td>
                      )}
                      <td style={{ ...tdBase, textAlign: 'right' }}>
                        {/* Índice de saúde não aparece em rascunho (é provisório/parcial). */}
                        {r.STATUS !== 'Rascunho' && r.INDICE_SAUDE != null ? (
                          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: 15, fontWeight: 800, color: corSaude(r.CLASSIFICACAO_SAUDE), fontVariantNumeric: 'tabular-nums' }}>{r.INDICE_SAUDE}%</span>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>{r.CLASSIFICACAO_SAUDE}</span>
                          </span>
                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ ...tdBase, fontSize: 13, color: '#475569', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {dataRef(r) ? dataRef(r).split('-').reverse().join('/') : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                          {/* Rascunho: "Continuar" e "Excluir" — só o autor (ou Admin). */}
                          {podeMexer && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setContinuarRascunho({ id: r.ID, idTemplate: r.ID_TEMPLATE }); }}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>
                                <Edit3 size={13} /> Continuar
                              </button>
                              <button
                                onClick={(e) => excluirRascunho(r, e)} disabled={excluindoId === r.ID} title="Excluir rascunho"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 800, fontSize: 12.5, cursor: excluindoId === r.ID ? 'wait' : 'pointer' }}>
                                <Trash2 size={13} /> {excluindoId === r.ID ? 'Excluindo…' : 'Excluir'}
                              </button>
                            </>
                          )}
                          <ChevronRight size={18} color="#cbd5e1" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const corResultado = (r) => ({
  CERTIFICADO: '#059669', 'CERTIFICADO COM RESTRIÇÕES': '#d97706', 'NÃO CERTIFICADO': '#dc2626',
}[r] || '#64748b');

const brl = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataHoraBR = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '');
// Quantidade vem DECIMAL do banco: sem isto, "1 peça" aparece como "1.00" na tela.
const qtd = (v) => (Number(v) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });

// Cartão-seção padrão do detalhe (mesma moldura dos blocos de sistema/NC).
const cartao = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 };
const tituloSecao = { margin: '0 0 10px', fontSize: 14, fontWeight: 800, color: '#475569' };
const thPeca = { padding: '6px 8px', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdPeca = { padding: '7px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#334155', verticalAlign: 'top' };

// Peças do relatório — a MESMA lista que já saía no PDF, agora também na tela
// (até 2026-08-17 o detalhe pulava as duas: quem quisesse saber o que foi pedido
// ou trocado tinha de baixar o PDF).
//
// `comValor` separa as duas naturezas: a lista do diagnóstico é o que PRECISA ser
// comprado (ainda sem preço confirmado); a da certificação é o que foi trocado, e
// aí o R$ existe e soma. Por isso o total só aparece nessa.
//
// ⚠️ Origem 'Central' = peça escolhida no catálogo da Central de Pedidos; NULL em
// relatório anterior à feature (2026-07-20) → lê-se como 'Fora', igual ao resto do
// módulo. O que vale é o snapshot gravado na linha, não o catálogo de hoje.
const TabelaPecas = ({ titulo, sub, itens, comValor, acao = null, vazio = null }) => {
  // Sem itens só some quando NÃO há ação: com o botão de adicionar, a seção
  // precisa existir mesmo vazia — é justamente no relatório sem peça nenhuma
  // que o consultor mais precisa incluir uma (7 relatórios finalizados estão
  // nessa situação hoje).
  if (!itens.length && !acao) return null;
  const total = itens.reduce((s, p) => s + (Number(p.VALOR) || 0) * (Number(p.QUANTIDADE) || 1), 0);
  return (
    <div style={cartao}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <h4 style={{ ...tituloSecao, margin: 0, flex: 1 }}>
          {titulo} <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>· {itens.length} {itens.length === 1 ? 'item' : 'itens'}{sub ? ` · ${sub}` : ''}</span>
        </h4>
        {acao}
      </div>
      {!itens.length && (
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#94a3b8' }}>{vazio || 'Nenhuma peça.'}</p>
      )}
      {itens.length > 0 && (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          {/* ⚠️ O `background` explícito é obrigatório: o CSS global do portal
              pinta TODO thead de amarelo da marca (src/index.css), e sem isto o
              cabeçalho da tabela sai amarelo com texto vermelho no meio do card. */}
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={{ ...thPeca, textAlign: 'left' }}>Código</th>
              <th style={{ ...thPeca, textAlign: 'left' }}>Descrição</th>
              <th style={{ ...thPeca, textAlign: 'left' }}>Fornecedor</th>
              <th style={{ ...thPeca, textAlign: 'right' }}>Qtd</th>
              <th style={{ ...thPeca, textAlign: 'left' }}>Motivo</th>
              {comValor && <th style={{ ...thPeca, textAlign: 'right' }}>Valor unit.</th>}
            </tr>
          </thead>
          <tbody>
            {itens.map((p) => {
              const central = p.ORIGEM === 'Central';
              return (
                <tr key={p.ID}>
                  <td style={{ ...tdPeca, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{p.CODIGO || '—'}</td>
                  <td style={tdPeca}>
                    <div>{p.DESCRICAO || '—'}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '2px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 800,
                        background: central ? '#eef2ff' : '#f1f5f9', color: central ? '#4338ca' : '#64748b',
                      }} title={central ? 'Peça do catálogo da Central de Pedidos' : 'Peça digitada fora do catálogo'}>
                        {central ? 'Central de Pedidos' : 'Fora do catálogo'}
                      </span>
                      {p.CATALOGO_FAMILIA && <span style={{ fontSize: 11, color: '#94a3b8' }}>{p.CATALOGO_FAMILIA}</span>}
                      {/* Peça acrescentada depois do diagnóstico — a marca existe
                          porque o relatório (e o PDF) podem já estar assinados. */}
                      {p.INCLUIDA_EM && (
                        <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 800, background: '#fef3c7', color: '#92400e' }}
                          title={`Não estava no diagnóstico: incluída em ${dataHoraBR(p.INCLUIDA_EM)}${p.INCLUIDA_POR ? ` por ${p.INCLUIDA_POR}` : ''}`}>
                          incluída depois
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={tdPeca}>{p.FORNECEDOR || '—'}</td>
                  <td style={{ ...tdPeca, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{qtd(p.QUANTIDADE)}</td>
                  <td style={{ ...tdPeca, color: '#64748b' }}>{p.MOTIVO || '—'}</td>
                  {comValor && <td style={{ ...tdPeca, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{brl(p.VALOR)}</td>}
                </tr>
              );
            })}
            {comValor && (
              <tr>
                <td colSpan={5} style={{ ...tdPeca, textAlign: 'right', fontWeight: 800, borderBottom: 'none' }}>Total</td>
                <td style={{ ...tdPeca, textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums', borderBottom: 'none' }}>{brl(total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

// Perfis que podem acrescentar peça depois do diagnóstico — os mesmos que
// preenchem o relatório (PERFIS_PREENCHE no backend).
const PERFIS_INCLUI_PECA = ['Admin', 'Manutencao', 'Diretoria'];

// Status que aceitam peça nova. ⚠️ ESPELHO de `certificacao-peca-posterior.js`
// (back) — o CRA não importa do `server/`. Mudou um, muda o outro: aqui isto só
// esconde o botão; quem recusa de verdade é o backend, com 409 e o motivo.
const STATUS_ACEITA_PECA = ['Em diagnóstico', 'Certificado com Ressalvas', 'Em execução'];

// Formulário de peça acrescentada DEPOIS do diagnóstico.
//
// Dois modos, como no formulário do diagnóstico: 'Central' (escolhe no catálogo
// da Central de Pedidos, e código/fornecedor/descrição vêm travados) e 'Fora'
// (digita à mão). ⚠️ A DESCRIÇÃO é o campo que torna a peça comprável — sem ela
// o item nunca chegaria ao módulo Compras; o backend recusa, e aqui o botão fica
// desabilitado para o usuário não descobrir isso depois de preencher tudo.
const FormNovaPeca = ({ idRelatorio, onCancelar, onIncluida }) => {
  const [origem, setOrigem] = useState('Central');
  const [peca, setPeca] = useState({ id_catalogo: null, codigo: '', descricao: '', fornecedor: '', familia: '' });
  const [quantidade, setQuantidade] = useState(1);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const limparPeca = () => setPeca({ id_catalogo: null, codigo: '', descricao: '', fornecedor: '', familia: '' });
  const trocarOrigem = (nova) => { setOrigem(nova); limparPeca(); };

  const salvar = async () => {
    setSalvando(true); setErro('');
    try {
      const r = await fetch(`${API}/${idRelatorio}/pecas-comprar`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origem, id_catalogo: peca.id_catalogo, codigo: peca.codigo, descricao: peca.descricao,
          fornecedor: peca.fornecedor, quantidade, motivo,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || 'Erro ao incluir a peça.');
      onIncluida(d?.message || 'Peça incluída.');
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  };

  const campo = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' };
  const escolhida = !!String(peca.descricao || '').trim();

  return (
    <div style={{ border: '1px solid #c7d2fe', background: '#f8faff', borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5, color: '#3730a3' }}>Nova peça a comprar</strong>
        <span style={{ fontSize: 12, color: '#64748b' }}>vai para a solicitação de compra e o setor de Compras é avisado</span>
        <button onClick={onCancelar} title="Cancelar"
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'inline-flex' }}>
          <X size={16} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {['Central', 'Fora'].map((o) => (
          <button key={o} onClick={() => trocarOrigem(o)} style={{
            padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 800, cursor: 'pointer',
            border: `1px solid ${origem === o ? '#4f46e5' : '#e2e8f0'}`,
            background: origem === o ? '#eef2ff' : '#fff', color: origem === o ? '#4338ca' : '#64748b',
          }}>
            {o === 'Central' ? 'Central de Pedidos' : 'Fora do catálogo'}
          </button>
        ))}
      </div>

      {origem === 'Central' ? (
        escolhida ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>{peca.descricao}</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>{peca.codigo} · {peca.fornecedor}{peca.familia ? ` · ${peca.familia}` : ''}</span>
            <button onClick={limparPeca} style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>trocar</button>
          </div>
        ) : (
          <div style={{ marginBottom: 10 }}>
            <SelecaoPecaCentral onEscolher={(c) => setPeca({
              id_catalogo: c.ID, codigo: c.CODIGO || '', descricao: c.DESCRICAO || '',
              fornecedor: c.FORNECEDOR || '', familia: c.FAMILIA || '',
            })} />
          </div>
        )
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 10 }}>
          <input style={campo} placeholder="Código" value={peca.codigo} onChange={(e) => setPeca((p) => ({ ...p, codigo: e.target.value }))} />
          <input style={campo} placeholder="Descrição *" value={peca.descricao} onChange={(e) => setPeca((p) => ({ ...p, descricao: e.target.value }))} />
          <input style={campo} placeholder="Fornecedor" value={peca.fornecedor} onChange={(e) => setPeca((p) => ({ ...p, fornecedor: e.target.value }))} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12.5, color: '#64748b' }}>Qtd
          <input type="number" min={1} value={quantidade} onChange={(e) => setQuantidade(e.target.value)}
            style={{ ...campo, width: 80, marginLeft: 6 }} />
        </label>
        <input style={{ ...campo, flex: 1, minWidth: 160 }} placeholder="Motivo (ex.: Desgaste)" maxLength={50}
          value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        <button onClick={salvar} disabled={!escolhida || salvando} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none',
          fontWeight: 800, fontSize: 13, color: '#fff', background: !escolhida || salvando ? '#cbd5e1' : '#4f46e5',
          cursor: !escolhida || salvando ? 'not-allowed' : 'pointer',
        }} title={!escolhida ? 'Informe a descrição da peça' : ''}>
          <Check size={15} /> {salvando ? 'Incluindo…' : 'Incluir peça'}
        </button>
      </div>

      {erro && <div style={{ marginTop: 10, padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{erro}</div>}
    </div>
  );
};

// Uma linha do registro fotográfico: o mesmo ângulo ANTES e DEPOIS, lado a lado.
// É a comparação que o consultor faz de fato — o PDF as separa em duas páginas e
// a tela, até 2026-08-17, dava só uma lista de links soltos.
//
// ⚠️ A chave é o SLOT (o ângulo), não a ordem: no backend SLOTS_DEPOIS = SLOTS_ANTES,
// então o mesmo nome identifica o par. Slot fotografado só num dos momentos aparece
// com a metade vazia — ausência de foto DEPOIS é informação, não erro de tela.
const ParFotos = ({ apiBase, slot, antes, depois, tamanho }) => {
  const vazio = (rotulo) => (
    <div style={{
      width: tamanho, height: tamanho, borderRadius: 6, border: '1px dashed #e2e8f0', background: '#f8fafc',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, color: '#cbd5e1', textAlign: 'center', padding: 6, boxSizing: 'border-box',
    }}>sem foto {rotulo}</div>
  );
  const coluna = (rotulo, foto, cor, borda) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: cor, textTransform: 'uppercase', marginBottom: 4 }}>{rotulo}</div>
      {foto
        ? <ImagemSharePoint apiBase={apiBase} url={foto.FOTO_URL} alt={`${slot} — ${rotulo}`} size={tamanho} borderColor={borda} />
        : vazio(rotulo.toLowerCase())}
    </div>
  );
  return (
    <div style={{ border: '1px solid #f1f5f9', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>{slot}</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {coluna('Antes', antes, '#b45309', '#fde68a')}
        {coluna('Depois', depois, '#047857', '#a7f3d0')}
      </div>
    </div>
  );
};

const Detalhe = ({ id, onVoltar, onCertificar }) => {
  const compacto = useEhCompacto();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [validando, setValidando] = useState('');
  // Texto digitado nas observações de validação, por `${etapa}_${papel}` (antes
  // de validar). A observação vai JUNTO com o clique em "Validar" e trava depois.
  const [obsInput, setObsInput] = useState({});
  // Inclusão de peça DEPOIS do diagnóstico: formulário aberto? e o aviso do que
  // aconteceu com a solicitação de compra (criada agora × item acrescentado a
  // uma que já existia) — a diferença importa para quem vai cobrar a compra.
  const [incluindoPeca, setIncluindoPeca] = useState(false);
  const [avisoPeca, setAvisoPeca] = useState('');

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/${id}`, { headers: auth() });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro.');
      setDados(data);
    } catch (e) { setErro(e.message); }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  // Valida uma ETAPA ('diagnostico'|'certificacao') por PAPEL, enviando a
  // observação do papel junto. O back grava assinatura + observação e regrava o
  // PDF; recarrega para refletir o estado travado.
  const validar = async (etapa, papel) => {
    const chave = `${etapa}_${papel}`;
    setValidando(chave);
    try {
      const resp = await fetch(`${API}/${id}/validar/${etapa}/${papel}`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ observacao: (obsInput[chave] || '').trim() || null }),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d?.message || 'Erro ao validar.');
      await carregar();
    } catch (e) { alert(e.message); }
    finally { setValidando(''); }
  };
  const dataHora = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '');

  if (erro) return (
    <div><button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
      <div style={{ marginTop: 12, padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{erro}</div></div>
  );
  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const { relatorio: r, componentes, naoConformidades, fotos, pecas = [], pecasComprar = [] } = dados;
  const perfil = perfilNoModulo(JSON.parse(localStorage.getItem('user') || '{}').perfil, 'manutencao') || '';
  const isAdmin = perfil === 'Admin';
  const podeCertificar = ['Admin', 'Manutencao'].includes(perfil) && ['Em diagnóstico', 'Em execução'].includes(r.STATUS);

  // GATE do diagnóstico: a Etapa 2 só inicia depois que gerente E consultor
  // validaram o diagnóstico. Enquanto isso, o botão "Certificar" fica travado.
  const diagValidado = !!(r.VALID_DIAG_GERENTE_NOME && r.VALID_DIAG_CONSULTOR_NOME);
  const gateCertificacao = r.STATUS === 'Em diagnóstico' && !diagValidado;

  // O diagnóstico já finalizado (existe relatório): habilita o painel de validação
  // da Etapa 1. Rascunho/Cancelado ainda não têm diagnóstico a validar.
  const temDiagnostico = ['Em diagnóstico', 'Em execução', 'Certificado com Ressalvas', 'Certificado'].includes(r.STATUS);
  const temCertificacao = ['Certificado com Ressalvas', 'Certificado'].includes(r.STATUS);

  // Renderiza um painel de validação (diagnóstico ou certificação). Cada papel tem
  // uma observação que vai JUNTO com o clique em "Validar" e TRAVA depois (só Admin
  // reedita). `linhas` traz, por papel, as colunas já gravadas no relatório.
  const renderPainelValidacao = ({ titulo, sub, etapa, linhas }) => (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 800, color: '#475569' }}>{titulo}</h4>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#64748b' }}>{sub}</p>
      <div style={{ display: 'grid', gap: 14 }}>
        {linhas.map((v) => {
          const chave = `${etapa}_${v.papel}`;
          const jaValidou = !!v.nome;
          const podeAgir = v.perfis.includes(perfil);
          const editavel = podeAgir && (!jaValidou || isAdmin);       // Admin reedita mesmo travado
          const valorObs = obsInput[chave] !== undefined ? obsInput[chave] : (v.obs || '');
          return (
            <div key={v.papel} style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', minWidth: 180 }}>{v.label}</span>
                {jaValidou ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#065f46', fontWeight: 700 }}>
                    <Check size={16} /> Validado por {v.nome} em {dataHora(v.data)}
                  </span>
                ) : podeAgir ? null : (
                  <span style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Aguardando validação</span>
                )}
              </div>
              {editavel ? (
                <>
                  <textarea
                    value={valorObs}
                    onChange={(e) => setObsInput((o) => ({ ...o, [chave]: e.target.value }))}
                    placeholder="Observação (opcional) — trava ao validar"
                    style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box', minHeight: 60, resize: 'vertical', marginBottom: 8 }} />
                  <button onClick={() => validar(etapa, v.papel)} disabled={validando === chave} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                    background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800,
                    cursor: validando === chave ? 'wait' : 'pointer',
                  }}>
                    <Check size={16} /> {validando === chave ? 'Salvando…' : (jaValidou ? `Regravar (Admin)` : `Validar como ${v.label}`)}
                  </button>
                </>
              ) : (v.obs ? (
                <p style={{ margin: 0, fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>
                  <strong style={{ color: '#64748b' }}>Observação:</strong> {v.obs}
                </p>
              ) : null)}
            </div>
          );
        })}
      </div>
    </div>
  );

  // O documento se adapta ao status: só quando a Etapa 2 conclui (RESULTADO_CERTIFICACAO
  // preenchido) ele é o "Certificado de Conformidade Técnica"; nas etapas anteriores
  // (diagnóstico feito) é o "Relatório de Diagnóstico". O PDF existe a partir do momento
  // em que o diagnóstico é finalizado — no Rascunho ainda não há nada gerado.
  // Botão "Adicionar peça": perfil que preenche + status que aceita. A recusa de
  // verdade é do backend (409 com o motivo); aqui só evitamos oferecer o que
  // seria negado.
  const podeIncluirPeca = PERFIS_INCLUI_PECA.includes(perfil) && STATUS_ACEITA_PECA.includes(r.STATUS);

  const ehCertificado = !!r.RESULTADO_CERTIFICACAO;
  const temPdf = r.STATUS !== 'Rascunho';
  const rotuloPdf = ehCertificado ? 'Certificado (PDF)' : 'Diagnóstico (PDF)';

  // Download do PDF: a rota exige token, então busca via fetch e dispara o blob.
  const baixarPdf = async () => {
    try {
      const resp = await fetch(`${API}/${r.ID}/pdf`, { headers: auth() });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = `${ehCertificado ? 'Certificado' : 'Diagnostico'}_${r.ID}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { alert('Erro ao gerar o PDF.'); }
  };
  // Agrupa componentes por sistema preservando a ordem.
  const sistemas = [];
  const idx = new Map();
  for (const c of componentes) {
    if (!idx.has(c.ID_SISTEMA)) { idx.set(c.ID_SISTEMA, sistemas.length); sistemas.push({ nome: c.SISTEMA, peso: c.PESO, itens: [] }); }
    sistemas[idx.get(c.ID_SISTEMA)].itens.push(c);
  }

  // Só as peças realmente preenchidas — MESMO critério do PDF (pdf-certificacao.js):
  // as linhas em branco que sobram do formulário não viram item na lista.
  const pecasComprarReais = pecasComprar.filter((p) => p.DESCRICAO || p.MOTIVO || p.FORNECEDOR);
  const pecasReais = pecas.filter((p) => p.CODIGO || p.DESCRICAO || p.FORNECEDOR || p.MOTIVO || Number(p.VALOR));

  // Ângulos fotografados, na ordem em que foram gravados (o ANTES vem primeiro,
  // então a galeria segue a ordem do diagnóstico). Cada ângulo rende um par.
  const slotsFoto = [];
  for (const f of fotos) if (!slotsFoto.includes(f.SLOT)) slotsFoto.push(f.SLOT);
  const fotoDe = (slot, momento) => fotos.find((f) => f.SLOT === slot && f.MOMENTO === momento);
  const temFotoDepois = fotos.some((f) => f.MOMENTO === 'depois');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{r.EQUIPAMENTO} · {r.LOJA} · #{r.ID}</h3>
        {temPdf && (
          <button onClick={baixarPdf} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
            background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
          }}>
            <FileDown size={16} /> {rotuloPdf}
          </button>
        )}
        {podeCertificar && (
          <button
            onClick={() => onCertificar(r.ID)}
            disabled={gateCertificacao}
            title={gateCertificacao ? 'Aguardando validação do diagnóstico (gerente e consultor)' : ''}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
              background: gateCertificacao ? '#cbd5e1' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800,
              cursor: gateCertificacao ? 'not-allowed' : 'pointer',
            }}>
            <Award size={16} /> {r.STATUS === 'Em execução' ? 'Continuar certificação' : 'Certificar (Etapa 2)'}
          </button>
        )}
      </div>

      {/* Gate visível: enquanto o diagnóstico não for validado pelos dois papéis,
          a certificação não pode iniciar. */}
      {podeCertificar && gateCertificacao && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
          A <strong>certificação (Etapa 2)</strong> só pode iniciar depois que o <strong>gerente</strong> e o <strong>consultor de manutenção</strong> validarem o diagnóstico abaixo.
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Índice de Saúde</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: corSaude(r.CLASSIFICACAO_SAUDE) }}>{r.INDICE_SAUDE}% · {r.CLASSIFICACAO_SAUDE}</div>
          </div>
          {r.RESULTADO_CERTIFICACAO && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Resultado</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: corResultado(r.RESULTADO_CERTIFICACAO) }}>{r.RESULTADO_CERTIFICACAO}</div>
              {r.DATA_VALIDADE && <div style={{ fontSize: 12, color: '#94a3b8' }}>validade até {String(r.DATA_VALIDADE).slice(0, 10)}</div>}
            </div>
          )}
          <div style={{ fontSize: 13, color: '#475569' }}>
            Status: <strong>{r.STATUS}</strong> <span style={{ fontStyle: 'italic', color: '#64748b' }}>({descricaoEtapa(r.STATUS)})</span><br />
            {r.NOME_VERSAO}{r.NUMERO_ATIVO ? ` · Ativo ${r.NUMERO_ATIVO}` : ''}<br />
            {r.GERENTE_NEGOCIOS && <>GN: {r.GERENTE_NEGOCIOS} · </>}{r.GERENTE_PLANTAO && <>GP: {r.GERENTE_PLANTAO}</>}
          </div>
        </div>
      </div>

      {/* Validação do DIAGNÓSTICO (Etapa 1) — GATE da certificação. Aparece assim
          que o diagnóstico é finalizado. Gerente e Consultor validam; a observação
          de cada um vai junto e trava (só Admin reedita). Enquanto os dois não
          validam, a Etapa 2 não inicia. */}
      {temDiagnostico && renderPainelValidacao({
        titulo: 'Validação do diagnóstico (Etapa 1)',
        sub: 'Gerente e consultor de manutenção validam o diagnóstico. A certificação (Etapa 2) só inicia após as duas validações. A observação é opcional e trava ao validar.',
        etapa: 'diagnostico',
        linhas: [
          { papel: 'gerente', label: 'Gerente', nome: r.VALID_DIAG_GERENTE_NOME, data: r.VALID_DIAG_GERENTE_DATA, obs: r.OBS_DIAG_GERENTE, perfis: ['Gerente', 'Admin'] },
          { papel: 'consultor', label: 'Consultor de Manutenção', nome: r.VALID_DIAG_CONSULTOR_NOME, data: r.VALID_DIAG_CONSULTOR_DATA, obs: r.OBS_DIAG_CONSULTOR, perfis: ['Manutencao', 'Admin'] },
        ],
      })}

      {/* Validação da CERTIFICAÇÃO (Etapa 2) — a partir do certificado provisório
          ('Certificado com Ressalvas'). Quando os dois validam, o status vira
          'Certificado' (o painel segue visível com as duas assinaturas). */}
      {temCertificacao && renderPainelValidacao({
        titulo: 'Validação da certificação (Etapa 2)',
        sub: 'Gerente e consultor de manutenção assinam o certificado. Quando os dois validam, o relatório passa a "Certificado" (definitivo). A observação é opcional e trava ao validar.',
        etapa: 'certificacao',
        linhas: [
          { papel: 'gerente', label: 'Gerente', nome: r.VALID_GERENTE_NOME, data: r.VALID_GERENTE_DATA, obs: r.OBS_CERT_GERENTE, perfis: ['Gerente', 'Admin'] },
          { papel: 'consultor', label: 'Consultor de Manutenção', nome: r.VALID_CONSULTOR_NOME, data: r.VALID_CONSULTOR_DATA, obs: r.OBS_CERT_CONSULTOR, perfis: ['Manutencao', 'Admin'] },
        ],
      })}

      {sistemas.map((s, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 800, color: '#475569' }}>{s.nome} <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>· peso {s.peso}</span></h4>
          <div style={{ display: 'grid', gap: 6 }}>
            {s.itens.map((c) => (
              <div key={c.ID_COMPONENTE} style={{ fontSize: 13, borderBottom: '1px solid #f1f5f9', paddingBottom: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, color: '#0f172a' }}>
                    {c.COMPONENTE}
                    {(c.BLOQUEANTE === 1) && <ShieldAlert size={12} color="#b91c1c" style={{ marginLeft: 4, verticalAlign: 'middle' }} />}
                  </span>
                  <span style={{ fontWeight: 700, color: CLASSIF_COR[c.CLASSIFICACAO] }}>{CLASSIF_LABEL[c.CLASSIFICACAO]}</span>
                  {/* Até 3 fotos por componente — um link por foto (numera se houver mais de 1). */}
                  {(c.FOTOS || (c.FOTO_URL ? [c.FOTO_URL] : [])).map((u, i, arr) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <ExternalLink size={14} />{arr.length > 1 ? <span style={{ fontSize: 11, fontWeight: 700 }}>{i + 1}</span> : null}
                    </a>
                  ))}
                </div>
                {c.ACAO_OBSERVACAO && <div style={{ marginTop: 2, color: '#64748b', fontSize: 12 }}>↳ {c.ACAO_OBSERVACAO}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {naoConformidades.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 800, color: '#475569' }}>Não Conformidades</h4>
          <div style={{ display: 'grid', gap: 6 }}>
            {naoConformidades.map((nc) => (
              <div key={nc.ID} style={{ fontSize: 13, color: '#475569' }}>
                <strong>{nc.CRITICIDADE}</strong> · {nc.COMPONENTE}{nc.PROBLEMA ? ` — ${nc.PROBLEMA}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Peças levantadas no diagnóstico (Etapa 1) — o que a loja precisa comprar.
          É a mesma lista que alimenta a solicitação no módulo Compras.
          Desde 2026-08-18 a Manutenção pode ACRESCENTAR peça aqui: quem descobre
          que falta é o consultor executando o serviço, não no diagnóstico. */}
      {avisoPeca && (
        <div style={{ padding: '10px 14px', background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {avisoPeca}
        </div>
      )}
      {incluindoPeca && (
        <FormNovaPeca
          idRelatorio={r.ID}
          onCancelar={() => setIncluindoPeca(false)}
          onIncluida={(msg) => { setIncluindoPeca(false); setAvisoPeca(msg); carregar(); }}
        />
      )}
      <TabelaPecas
        titulo="Peças a comprar (diagnóstico)"
        itens={pecasComprarReais}
        comValor={false}
        vazio="Nenhuma peça foi levantada no diagnóstico."
        acao={podeIncluirPeca && !incluindoPeca ? (
          <button onClick={() => { setIncluindoPeca(true); setAvisoPeca(''); }} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8,
            fontWeight: 800, fontSize: 13, cursor: 'pointer',
          }} title="A peça vai para a solicitação de compra e o setor de Compras é avisado">
            <Plus size={15} /> Adicionar peça
          </button>
        ) : null}
      />

      {/* Peças efetivamente trocadas na certificação (Etapa 2), com o R$ confirmado. */}
      <TabelaPecas titulo="Peças substituídas (certificação)" itens={pecasReais} comValor />

      {fotos.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 800, color: '#475569' }}>Registro fotográfico</h4>
          <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#64748b' }}>
            Cada ângulo com o <strong>antes</strong> (diagnóstico) e o <strong>depois</strong> (certificação) lado a lado. Clique para ampliar.
            {!temFotoDepois && ' As fotos do depois entram na Etapa 2.'}
          </p>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: compacto ? '1fr' : 'repeat(auto-fill, minmax(380px, 1fr))' }}>
            {slotsFoto.map((slot) => (
              <ParFotos
                key={slot}
                apiBase={API}
                slot={slot}
                antes={fotoDe(slot, 'antes')}
                depois={fotoDe(slot, 'depois')}
                tamanho={compacto ? 130 : 160}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificacaoHistorico;
